import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setClient } from "../../../../redux/features/jobCreateSlice";
import { ClientsTable } from "../../../clients/clientsTable";
import { useClients } from "../../../clients/hooks/useClients";
import { useModals } from "../../../../context/ModalContext";
import { usePatchJob } from "../../../clients/hooks/usePatchJob";

export function SelectJobCoverageStep({ jobId, clientUidSelected, currentCoverageAreaId }) {
  const selectedClient = useSelector((s) => s.jobCreateStepper.client);
  const dispatch = useDispatch();
  const { clients, getClientByUid } = useClients();
  const { closeByKey } = useModals();
  const { mutate: patchJob } = usePatchJob();

  const hasSetClientRef = useRef(false);
  const lastClientUidRef = useRef(null);

  useEffect(() => {
    if (!clientUidSelected) return;
    getClientByUid(clientUidSelected);
  }, [clientUidSelected, getClientByUid]);

  const clientObject = useMemo(() => {
    if (!clientUidSelected || !clients?.items?.length) return null;
    return clients.items.find((client) => client.uid === clientUidSelected) || null;
  }, [clientUidSelected, clients?.items]);

  const initialCoverageArea = useMemo(() => {
    if (!clientObject || !currentCoverageAreaId) return null;
    return clientObject.coverageAreas?.find((area) => area.id === currentCoverageAreaId) ?? null;
  }, [clientObject, currentCoverageAreaId]);

  useEffect(() => {
    if (lastClientUidRef.current !== clientUidSelected) {
      hasSetClientRef.current = false;
      lastClientUidRef.current = clientUidSelected;
    }
  }, [clientUidSelected]);

  useEffect(() => {
    if (!clientObject || hasSetClientRef.current) return;

    const clientWithCoverage = initialCoverageArea
      ? { ...clientObject, coverageArea: initialCoverageArea }
      : clientObject;

    dispatch(setClient(clientWithCoverage));
    hasSetClientRef.current = true;
  }, [clientObject, initialCoverageArea, dispatch]);

  const handleSelectCoverage = (clientWithCoverage) => {
    dispatch(setClient(clientWithCoverage));
  };

  const handleUpdateCoverage = () => {
    if (!selectedClient?.coverageArea?.id) return;

    patchJob(
      { jobId, payload: { coverageAreaId: selectedClient.coverageArea.id } },
      { onSuccess: () => closeByKey(`Job-Edit-Coverage-${jobId}`) },
    );
  };

  return (
    <ClientsTable
      allowSelected
      selectedClient={selectedClient}
      onSelectClientAndCoverage={handleSelectCoverage}
      disableMenuOptions
      clientUidFilter={clientUidSelected}
      disableInnerChilds={false}
      disableRecordClickable
      disableClientDetailsModal
      isUpdateJob
      jobId={jobId}
      onUpdateJob={handleUpdateCoverage}
      updateJobLabel="Update coverage area"
    />
  );
}
